// ---------------------------------------------
// UI CONSTANTS
// ---------------------------------------------
// Public, non-proprietary constants safe for the browser bundle.
// Proprietary prompt-building logic lives in promptCompiler.js (server-only).
// Track categories used by the filter chips on Dashboard and TrackSelector.
// A track can belong to multiple categories. Tracks with no categories only
// show when the All filter is active. IT Support leads the list because the
// A+ entry-level track is the most common starting point.
export const TRACK_CATEGORIES = ['IT Support', 'Cloud', 'Cybersecurity', 'Networking', 'Project Management'];

// Difficulty buckets surfaced by the course-library filter on TrackSelector.
// Ordering matches the learner's mental model: easiest first.
export const TRACK_DIFFICULTIES = ['Beginner', 'Intermediate', 'Advanced'];

// Certification prerequisite ladders — drives the Roadmap cert-path chain and
// career milestones. Keyed by track id; the array is the ordered ladder of the
// certs a learner needs to reach that role's cert (foundation → target). When a
// learner pins a role on Career Hub, the Roadmap expands the role's track via
// this map so the path shows EVERY cert needed for it (e.g. an MS-102 admin
// role → A+ → AB-900 → MS-102). Kept consistent with the onboarding ladders in
// certRecommendation.js. Tracks not listed stand alone (single-step path).
export const CAREER_PATHS = {
  // CompTIA core
  comptia_aplus:    ['comptia_aplus'],
  comptia_network:  ['comptia_aplus', 'comptia_network'],
  comptia_security: ['comptia_aplus', 'comptia_network', 'comptia_security'],
  comptia_linux:    ['comptia_aplus', 'comptia_linux'],
  comptia_pentest:  ['comptia_aplus', 'comptia_network', 'comptia_security', 'comptia_pentest'],
  // Microsoft Azure / M365
  az_900:           ['az_900'],
  az_104:           ['az_900', 'az_104'],
  ab_900:           ['ab_900'],
  ms_102:           ['comptia_aplus', 'ab_900', 'ms_102'],
  // AWS
  aws_ccp:          ['aws_ccp'],
  aws_saa:          ['aws_ccp', 'aws_saa'],
  aws_sap:          ['aws_ccp', 'aws_saa', 'aws_sap'],
  // Google
  google_it_support: ['google_it_support'],
  gcp_pca:          ['gcp_pca'],
  // Cisco networking
  cisco_ccna:       ['comptia_aplus', 'comptia_network', 'cisco_ccna'],
  cisco_ccie:       ['comptia_aplus', 'comptia_network', 'cisco_ccna', 'cisco_ccie'],
  // Advanced cybersecurity
  cissp:            ['comptia_aplus', 'comptia_network', 'comptia_security', 'cissp'],
  // Advanced Linux
  rhca:             ['comptia_aplus', 'comptia_linux', 'rhca'],
  // Project / service management (standalone)
  itil_foundation:  ['itil_foundation'],
  pmp:              ['pmp'],
};

// Typical US salary band per track's first job role — Roadmap signal pills +
// career-milestone nodes. Static reference figures (not live Adzuna).
export const TRACK_SALARY = {
  comptia_aplus:    '$34k–$55k',
  comptia_network:  '$55k–$78k',
  comptia_security: '$78k–$110k',
  az_900:           '$50k–$72k',
  az_104:           '$80k–$120k',
};

// Each track carries display metadata (label/badge/color/categories/difficulty)
// plus the learning-detail fields surfaced in the TrackSelector detail panel:
// description, exam_codes, passing_score, exam_fee, exam_url, job_roles.
// Live/beta tracks have full exam details; roadmap "Coming Soon" tracks carry
// description + job_roles only (no exam logistics until curriculum is seeded).
export const TRACKS = {
  comptia_aplus: {
    id: 'comptia_aplus', label: 'CompTIA A+', badge: 'Entry Level', color: '#3B82F6',
    exams: ['Core 1 (220-1201)', 'Core 2 (220-1202)'], categories: ['IT Support'], difficulty: 'Beginner',
    description: 'Master hardware, software, networking, and troubleshooting fundamentals — the baseline credential for IT support roles.',
    exam_codes: ['220-1201', '220-1202'], passing_score: 675, exam_fee: '$253 per exam',
    exam_url: 'https://www.comptia.org/certifications/a',
    job_roles: ['Help Desk Technician', 'IT Support Specialist', 'Field Service Technician', 'Desktop Support Analyst', 'Technical Support Specialist'],
  },
  comptia_network: {
    id: 'comptia_network', label: 'CompTIA Network+', badge: 'Networking', color: '#0891B2',
    exams: ['N10-009'], status: 'active', moduleCount: 5, subtitle: 'N10-009', categories: ['Networking'], difficulty: 'Intermediate',
    description: 'Build fluency in network infrastructure, protocols, and troubleshooting — required for network and systems roles.',
    exam_codes: ['N10-009'], passing_score: 720, exam_fee: '$369',
    exam_url: 'https://www.comptia.org/certifications/network',
    job_roles: ['Network Administrator', 'NOC Technician', 'Systems Administrator', 'Network Support Specialist', 'IT Infrastructure Analyst'],
  },
  comptia_security: {
    id: 'comptia_security', label: 'CompTIA Security+', badge: 'Security', color: '#7C3AED',
    exams: ['SY0-701'], status: 'active', moduleCount: 5, subtitle: 'SY0-701', categories: ['Cybersecurity'], difficulty: 'Intermediate',
    description: 'Cover threat analysis, vulnerability management, and security operations — the entry credential for cybersecurity roles.',
    exam_codes: ['SY0-701'], passing_score: 750, exam_fee: '$404',
    exam_url: 'https://www.comptia.org/certifications/security',
    job_roles: ['Security Analyst', 'SOC Analyst', 'IT Security Specialist', 'Cybersecurity Technician', 'Information Security Analyst'],
  },
  az_900: {
    id: 'az_900', label: 'Microsoft AZ-900', badge: 'Cloud Fundamentals', color: '#0078D4',
    exams: ['AZ-900'], status: 'active', moduleCount: 3, subtitle: 'AZ-900', categories: ['Cloud'], difficulty: 'Beginner',
    description: 'Understand core Azure cloud concepts, services, and pricing — the foundation for all Microsoft cloud certifications.',
    exam_codes: ['AZ-900'], passing_score: 700, exam_fee: '$165',
    exam_url: 'https://learn.microsoft.com/en-us/credentials/certifications/azure-fundamentals/',
    job_roles: ['Cloud Support Associate', 'IT Administrator', 'Systems Engineer', 'Cloud Operations Analyst', 'Junior Cloud Engineer'],
  },
  az_104: {
    id: 'az_104', label: 'Microsoft AZ-104', badge: 'Cloud Admin', color: '#0078D4',
    exams: ['AZ-104'], status: 'active', moduleCount: 5, subtitle: 'AZ-104', categories: ['Cloud'], difficulty: 'Intermediate',
    description: 'Administer Azure infrastructure including virtual machines, storage, networking, and identity — required for Azure administrator roles.',
    exam_codes: ['AZ-104'], passing_score: 700, exam_fee: '$165',
    exam_url: 'https://learn.microsoft.com/en-us/credentials/certifications/azure-administrator/',
    job_roles: ['Azure Administrator', 'Cloud Administrator', 'Systems Administrator', 'Infrastructure Engineer', 'Cloud Operations Engineer'],
  },
  ab_900: {
    id: 'ab_900', label: 'Microsoft AB-900', badge: 'M365 AI Fundamentals', color: '#00A4EF',
    exams: ['AB-900'], status: 'beta', moduleCount: 3, subtitle: 'AB-900', categories: ['Cloud'], difficulty: 'Beginner',
    description: 'Get grounded in Microsoft 365 Copilot and AI fundamentals across the M365 suite — an entry point for AI-assisted productivity roles.',
    exam_codes: ['AB-900'], passing_score: 700, exam_fee: '$99',
    exam_url: 'https://learn.microsoft.com/en-us/credentials/certifications/copilot-and-agent-administration-fundamentals/',
    job_roles: ['M365 Administrator', 'Business Applications Analyst', 'Productivity Specialist', 'IT Support Specialist', 'Collaboration Engineer'],
  },

  // ── Roadmap placeholders ──
  // Surfaced in the course library so learners can see the planned catalog.
  // No status field → renders as "Coming Soon" via the Dashboard/Selector
  // fallback. Not selectable until curriculum is seeded. Description + job_roles
  // only — exam logistics are added when the track goes live.
  ms_102: {
    id: 'ms_102', label: 'Microsoft 365 Administrator (MS-102)', badge: 'M365 Admin', color: '#0078D4',
    exams: ['MS-102'], categories: ['Cloud'], difficulty: 'Intermediate',
    description: 'Administer Microsoft 365 tenants — identity, security, compliance, and workload management across the M365 suite.',
    job_roles: ['Microsoft 365 Administrator', 'M365 Engineer', 'Collaboration Administrator', 'Identity Administrator', 'Modern Workplace Engineer'],
  },
  google_it_support: {
    id: 'google_it_support', label: 'Google IT Support Pro', badge: 'IT Support', color: '#4285F4',
    exams: ['Google IT Support Cert'], categories: ['IT Support'], difficulty: 'Beginner',
    description: 'Job-ready IT support skills across troubleshooting, customer service, networking, operating systems, and security — a recognized on-ramp to entry-level IT.',
    job_roles: ['Help Desk Technician', 'IT Support Specialist', 'Desktop Support Technician', 'Technical Support Representative', 'Junior Systems Administrator'],
  },
  aws_ccp: {
    id: 'aws_ccp', label: 'AWS Cloud Practitioner', badge: 'Cloud Fundamentals', color: '#FF9900',
    exams: ['CLF-C02'], categories: ['Cloud'], difficulty: 'Beginner',
    description: 'Foundational understanding of AWS cloud concepts, services, security, and pricing — the starting credential for an AWS career path.',
    job_roles: ['Cloud Support Associate', 'Cloud Practitioner', 'IT Administrator', 'Solutions Sales Engineer', 'Junior Cloud Analyst'],
  },
  comptia_linux: {
    id: 'comptia_linux', label: 'CompTIA Linux+', badge: 'Linux', color: '#A21CAF',
    exams: ['XK0-005'], categories: ['IT Support'], difficulty: 'Intermediate',
    description: 'Configure, manage, and troubleshoot Linux systems — command line, scripting, security, and administration for server-side roles.',
    job_roles: ['Linux Administrator', 'Systems Administrator', 'DevOps Technician', 'Site Reliability Engineer', 'Infrastructure Engineer'],
  },
  aws_saa: {
    id: 'aws_saa', label: 'AWS Solutions Architect Associate', badge: 'Cloud Architect', color: '#FF9900',
    exams: ['SAA-C03'], categories: ['Cloud'], difficulty: 'Intermediate',
    description: 'Design resilient, cost-optimized, and secure architectures on AWS — the in-demand associate credential for cloud architecture work.',
    job_roles: ['Cloud Solutions Architect', 'Cloud Engineer', 'DevOps Engineer', 'Systems Architect', 'Cloud Infrastructure Engineer'],
  },
  comptia_pentest: {
    id: 'comptia_pentest', label: 'CompTIA PenTest+', badge: 'Offensive Security', color: '#DC2626',
    exams: ['PT0-003'], categories: ['Cybersecurity'], difficulty: 'Intermediate',
    description: 'Plan and execute penetration tests — scoping, reconnaissance, exploitation, and reporting for offensive security roles.',
    job_roles: ['Penetration Tester', 'Security Analyst', 'Vulnerability Analyst', 'Red Team Operator', 'Application Security Engineer'],
  },
  itil_foundation: {
    id: 'itil_foundation', label: 'ITIL 4 Foundation', badge: 'Service Management', color: '#10b981',
    exams: ['ITIL 4 Foundation'], categories: ['Project Management'], difficulty: 'Intermediate',
    description: 'Learn the ITIL 4 service-management framework — value streams, practices, and guiding principles for modern IT service delivery.',
    job_roles: ['Service Desk Manager', 'IT Service Manager', 'Process Analyst', 'Change Manager', 'IT Operations Coordinator'],
  },
  pmp: {
    id: 'pmp', label: 'PMP', badge: 'Project Management', color: '#0EA5E9',
    exams: ['PMP'], categories: ['Project Management'], difficulty: 'Intermediate',
    description: 'Master project management across predictive, agile, and hybrid approaches — the globally recognized credential for project leaders.',
    job_roles: ['Project Manager', 'Program Manager', 'Project Coordinator', 'Delivery Manager', 'PMO Analyst'],
  },
  cisco_ccna: {
    id: 'cisco_ccna', label: 'Cisco CCNA', badge: 'Cisco Networking', color: '#1BA0D7',
    exams: ['200-301'], categories: ['Networking'], difficulty: 'Advanced',
    description: 'Install, configure, and operate Cisco networks — routing, switching, security fundamentals, and automation for networking careers.',
    job_roles: ['Network Engineer', 'Network Administrator', 'Network Technician', 'Systems Engineer', 'NOC Engineer'],
  },
  cissp: {
    id: 'cissp', label: 'CISSP', badge: 'Security Pro', color: '#7C3AED',
    exams: ['CISSP'], categories: ['Cybersecurity'], difficulty: 'Advanced',
    description: 'Advanced, vendor-neutral security expertise across eight domains — the leading credential for senior cybersecurity professionals.',
    job_roles: ['Security Manager', 'Security Architect', 'Information Security Officer', 'Security Consultant', 'Security Operations Lead'],
  },
  aws_sap: {
    id: 'aws_sap', label: 'AWS Solutions Architect Pro', badge: 'Cloud Architect', color: '#FF9900',
    exams: ['SAP-C02'], categories: ['Cloud'], difficulty: 'Advanced',
    description: 'Design and evolve complex, enterprise-scale AWS architectures — the professional-tier credential for senior cloud architects.',
    job_roles: ['Senior Cloud Architect', 'Principal Solutions Architect', 'Cloud Infrastructure Lead', 'Enterprise Architect', 'Cloud Platform Engineer'],
  },
  cisco_ccie: {
    id: 'cisco_ccie', label: 'Cisco CCIE', badge: 'Expert Networking', color: '#1BA0D7',
    exams: ['CCIE Enterprise'], categories: ['Networking'], difficulty: 'Advanced',
    description: 'Expert-level network design and operations — the pinnacle Cisco certification for the most senior networking specialists.',
    job_roles: ['Network Architect', 'Principal Network Engineer', 'Network Consultant', 'Infrastructure Architect', 'Senior Systems Engineer'],
  },
  rhca: {
    id: 'rhca', label: 'Red Hat RHCA', badge: 'Linux Architect', color: '#EE0000',
    exams: ['Multi-exam RHCA path'], categories: ['IT Support'], difficulty: 'Advanced',
    description: 'The highest Red Hat certification — proven, hands-on expertise across enterprise Linux, automation, and cloud-native infrastructure.',
    job_roles: ['Linux Architect', 'Senior Systems Engineer', 'Automation Architect', 'Platform Engineer', 'Infrastructure Architect'],
  },
  gcp_pca: {
    id: 'gcp_pca', label: 'Google Cloud Architect', badge: 'Cloud Architect', color: '#34A853',
    exams: ['Pro Cloud Architect'], categories: ['Cloud'], difficulty: 'Advanced',
    description: 'Design, build, and manage scalable, secure solutions on Google Cloud — the professional credential for GCP solution architects.',
    job_roles: ['Cloud Architect', 'GCP Solutions Architect', 'Cloud Engineer', 'Infrastructure Architect', 'Cloud Platform Lead'],
  },
};

export const PHASE_META = {
  PROFILE:     { label: 'Profile',     color: '#a8a49e' },  // warm gray — calm orientation
  LESSON:      { label: 'Lesson',      color: '#2dd4bf' },  // teal — active learning
  CHECKPOINT:  { label: 'Checkpoint',  color: '#fbbf24' },  // warm amber — test moment
  REMEDIATION: { label: 'Remediation', color: '#3b82f6' },  // distinct blue — supportive repair, separate from lesson teal & checkpoint amber; mid-luminance, reads on both themes
  VALIDATION:  { label: 'Validation',  color: '#34d399' },  // earned green — success
};

export const MASTERY_WEIGHTS = {
  early:        { wA: 0.35, wS: 0.25, wT: 0.20, wR: 0.20 },
  intermediate: { wA: 0.25, wS: 0.35, wT: 0.30, wR: 0.10 },
  validation:   { wA: 0.20, wS: 0.30, wT: 0.40, wR: 0.10 },
};

export const MASTERY_THRESHOLDS = {
  advance:     { M: 0.85, A: 0.85, S: 0.80, T: 0.80 },
  conditional: { M: 0.70 },
  remediate:   { M: 0.70 },
};

export const XP_PER_LESSON_COMPLETE  = 50;
export const XP_PER_CHECKPOINT_PASS  = 20;
export const XP_PER_MASTERY          = 100;
export const XP_PER_LEVEL            = 500;
